import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { listStudentQuestions } from "../../../../api/studentQuestions"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import type {
  PaginationMeta,
  QuestionListFilter,
  StudentQuestionListItem,
} from "../types"

/** Stable key so list refetches when any filter field changes. */
export const serializeQuestionFilter = (filter: QuestionListFilter): string =>
  [
    filter.page_number,
    filter.per_page,
    filter.subject,
    [...(filter.hierarchy_ids || [])].map(String).sort().join(","),
    filter.level_1,
    filter.level_2,
    filter.level_3,
    filter.level_4,
    filter.keyword,
    filter.year,
    filter.paper,
    filter.complexity,
    filter.sort_by,
    filter.sort_order,
  ].join("|")

const useQuestionList = (filter: QuestionListFilter) => {
  const [items, setItems] = useState<StudentQuestionListItem[]>([])
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const requestId = useRef(0)
  const filterKey = useMemo(() => serializeQuestionFilter(filter), [filter])
  const filterRef = useRef(filter)
  filterRef.current = filter

  const refetch = useCallback(async (nextFilter?: QuestionListFilter) => {
    const active = nextFilter ?? filterRef.current
    const id = ++requestId.current
    setLoading(true)
    setError("")
    try {
      const res = await listStudentQuestions(active)
      if (id !== requestId.current) return
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to load questions")
      }
      setItems(Array.isArray(res.data.data) ? res.data.data : [])
      setPagination(res.data.pagination ?? null)
    } catch (err) {
      if (id !== requestId.current) return
      setItems([])
      setPagination(null)
      setError(getLearnerApiErrorMessage(err, "Failed to load questions"))
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    refetch(filterRef.current)
  }, [filterKey, refetch])

  const patchBookmark = useCallback(
    (questionId: string, isBookmarked: boolean) => {
      setItems((prev) =>
        prev.map((q) => (q.id === questionId ? { ...q, isBookmarked } : q))
      )
    },
    []
  )

  return { items, pagination, loading, error, refetch, patchBookmark }
}

export default useQuestionList
